/**
 * @file This file contains collections, subscribers, emit feature used for useSyncExternalStore react feature
 * @url https://dev.azure.com/EYCTCanvas/FAST.ATF/_wiki/wikis/FAST.ATF.wiki/3901/GenericState-a-state-management-strategy-to-avoid-unnecessary-re-rendering
 * @author Michael Agcaoili <Master.Michael.Angelo.Agcaoili@ey.com>
 * @version 1.0.0
 */

import {useSyncExternalStore} from 'react';

export const collections = new Map();
const subscriberList = new Map();
export const emitDelayer = new Map();
/**
 * This is used to clear the time out if it exists already.
 * @param {Object} key - this is the pointer reference where the state is stored
 */
///
/// This is used to delay emmit changes.
///defaultValue
export const clearTheTimeOut = (key) => {
	if (emitDelayer.has(key)) {
		clearTimeout(emitDelayer.get(key));
		emitDelayer.delete(key);
	}
};
/**
 * This function works together with useGenericState/useSyncExternalStore. This is what causes the rerender.
 * @param {Object} key - this is the pointer reference where the state is stored
 * @param {Object} delayMilliSeconds - optional, this is used to delay the useGenericState effects
 */
export const emitChange = (key, delayMilliSeconds = null) => {
	if (subscriberList.has(key)) {
		if (delayMilliSeconds && typeof window !== 'undefined') {
			clearTheTimeOut(key);
			emitDelayer.set(
				key,
				setTimeout(() => {
					for (const listener of subscriberList.get(key)) {
						listener();
					}
					clearTheTimeOut(key);
				}, delayMilliSeconds)
			);
		} else {
			for (const listener of subscriberList.get(key)) {
				listener();
			}
		}
	}
};
/**
 * This is a helper that acts similar to useSelector of redux, this is using useSyncExternalStore.
 * A helper designed to have small codes that helps on code reduction when used. This helper does not require you to make reducer, middleware, and action objects. It is just about key and value pairs.
 * @param {Object} key - this is the pointer reference where the state and rerender/subscriber function is stored. See collections and subscriberList map object
 * @param {Object} defaultValue - optional, this is the default value if the key does not exists from the collection
 * @param {Object} postEvent - optional, this is an added event where you can customize how you retrieve the data, or you want to execute additional code before returning the stored values, ex: filtered, conditional, etc
 * @return {*} returns the data from the collections specifically where the reference pointer key is pointing to, otherwise if you have a defaultValue, it will return the default value
 */
export const useGenericState = (key, defaultValue = null, postEvent = null) => {
	return useSyncExternalStore(
		(callback) => {
			if (!subscriberList.has(key)) {
				subscriberList.set(key, new Set());
			}
			subscriberList.get(key).add(callback);
			return () => {
				if (subscriberList.has(key)) {
					subscriberList.get(key).delete(callback);
				}
			};
		},
		() => {
			if (collections.has(key)) {
				if (postEvent) {
					return postEvent(collections.get(key));
				}
				return collections.get(key);
			}
			return defaultValue;
		},
		() => {
			/* 	This code part is where we do server-side rendering of data, since we are not yet doing hybrid-rendering, 
				this code will be here but not yet functioning. This is just a preparation and a pointer to remind us that 
				we can do server-side rendering of data too if we design it in the future.
			*/
			if (collections.has(key)) {
				if (postEvent) {
					return postEvent(collections.get(key));
				}
				return collections.get(key);
			}
			return defaultValue;
		}
	);
};
